import { useState, FC } from 'react';
import dayjs from 'dayjs';
import { getSatisfactionIcon } from '../../../../constants';
import './SatisfactionCalendar.scss';
import { Typography, Popover } from '@mui/material';
import AnswerSatisfactionPopup from '../../AnswerSatisfactionPopup/AnswerSatisfactionPopup';
import clsx from 'clsx';
import { EMASatisfactionType, SatisfactionByDate } from '@demind-inc/core';
import { useTranslation } from 'react-i18next';
import { formatDataDate, getWeekdayLabels } from '../../../../i18n';

interface SatisfactionCalendarProps {
  data: SatisfactionByDate[];
  selectedDate: dayjs.Dayjs;
  onSatisfactionChange: (date: string, satisfaction: EMASatisfactionType) => void;
}

const SatisfactionCalendar: FC<SatisfactionCalendarProps> = ({ data, selectedDate, onSatisfactionChange }) => {
  const { t, i18n } = useTranslation();
  const [overlayDate, setOverlayDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const monthDisplay = formatDataDate(i18n.language, selectedDate.toDate(), {
    month: 'long',
    year: 'numeric',
  });
  const weekdays = getWeekdayLabels(t, 'narrow');
  const firstDayOfMonth = selectedDate.startOf('month').day();
  const daysInMonth = selectedDate.daysInMonth();

  const satisfactionMap = Object.fromEntries(data.map((d) => [d.date, d.satisfaction?.value]));

  const handleCellClick = (dateStr, event) => {
    if (!dayjs(dateStr).isAfter(dayjs(), 'day')) {
      setOverlayDate(dateStr);
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setOverlayDate(null);
    setAnchorEl(null);
  };

  const handleSatisfactionSelect = (val) => {
    if (overlayDate) {
      onSatisfactionChange(overlayDate, val);
    }
    handlePopoverClose();
  };

  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    cells.push(<div key={`empty-${i}`} className="satisfaction-calendar__cell empty" />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = selectedDate
      .startOf('month')
      .add(day - 1, 'day')
      .format('YYYY-MM-DD');
    const satisfaction = satisfactionMap[dateStr];
    const isSelected = selectedDate.format('YYYY-MM-DD') === dateStr;
    const isToday = dayjs().format('YYYY-MM-DD') === dateStr;
    const isFutureDate = dayjs(dateStr).isAfter(dayjs(), 'day');

    cells.push(
      <div
        key={dateStr}
        className={clsx('satisfaction-calendar__cell', {
          selected: isSelected,
          today: isToday,
        })}
        onClick={(e) => handleCellClick(dateStr, e)}
        style={{
          cursor: isFutureDate ? 'not-allowed' : 'pointer',
        }}
      >
        <Typography variant="body2" className="satisfaction-calendar__date">
          {day}
        </Typography>
        {isFutureDate ? (
          <div className="satisfaction-calendar__cell-empty" />
        ) : (
          getSatisfactionIcon(
            satisfaction || 'neutral',
            satisfaction ? 'selected' : 'unselected',
            22
          )
        )}
      </div>
    );
  }

  return (
    <div className="satisfaction-calendar">
      <div className="satisfaction-calendar__header">
        <Typography variant="body1">{monthDisplay}</Typography>
      </div>
      <div className="satisfaction-calendar__weekdays">
        {weekdays.map((d,index) => (
          <Typography key={index} className="satisfaction-calendar__weekday" variant="body2">
            {d}
          </Typography>
        ))}
      </div>
      <div className="satisfaction-calendar__grid">{cells}</div>
      <Popover
        open={!!overlayDate}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ style: { borderRadius: '16px' } }}
      >
        <AnswerSatisfactionPopup
          defaultValue={overlayDate ? satisfactionMap[overlayDate] : null}
          onSelect={handleSatisfactionSelect}
        />
      </Popover>
    </div>
  );
};

export default SatisfactionCalendar;
